import { useMemo } from 'react';
import { type LucideIcon, Server } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useAsync } from '../hooks/useAsync';

/** A unified, already-resolved palette result (navigation or resource). */
export interface PaletteEntry {
  id: string;
  /** Display title (resolved — not an i18n key). */
  title: string;
  /** Context path / resource type (resolved). */
  context: string;
  route: string;
  icon: LucideIcon;
}

interface Page<T> {
  items: T[];
}
interface Device {
  id: string;
  name: string;
}

/**
 * Live resource results for the command palette (jump straight to a named resource,
 * not just a page). Permission-gated so it never fetches — or names — resources the
 * user cannot read, and fail-soft: a failed or empty request yields no results.
 *
 * Edge Devices are covered because they have a detail route (`/devices/:id`); the
 * return shape accepts more resource types as they gain detail pages. The list is
 * fetched once while the palette is open and filtered client-side, which is fine at
 * current scale; a server-side search endpoint can back this later without changing
 * the palette.
 */
export function useResourceEntries(
  query: string,
  enabled: boolean,
  edgeLabel: string,
): PaletteEntry[] {
  const { can } = useAuth();
  const devices = useAsync(
    () =>
      enabled && can('edge_device:read')
        ? api.get<Page<Device>>('/edge-devices?limit=200')
        : Promise.resolve(null),
    [enabled],
  );

  return useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return [];
    return (devices.data?.items ?? [])
      .filter((d) => (d.name ?? '').toLowerCase().includes(q))
      .slice(0, 5)
      .map((d) => ({
        id: `res-device-${d.id}`,
        title: d.name,
        context: edgeLabel,
        route: `/devices/${d.id}`,
        icon: Server,
      }));
  }, [devices.data, query, edgeLabel]);
}
